/* eslint-disable react-hooks/exhaustive-deps */
import React, { useEffect, useState } from "react";
import { PaymentElement } from "@stripe/react-stripe-js";
import { useStripe, useElements } from "@stripe/react-stripe-js";
import { useAuthState } from "../../utilities/contexts/auth-state-context";
import { PaymentCycle } from "../../models/enumerations/payment-cycle";
import { Loader } from "../loader/loader";
import OrganizationSubscriptionService from "../../utilities/services/organization-subscription-service";
import { WarningItem } from "../warning-item/warning-item";
import { Button } from "../button/button";
import { Tag as TagComponent } from "../tag/tag";
import OrganizationService from "../../utilities/services/organization-service";
import { PlanType } from "../../models/enumerations/plan-type";
import { UserLimitPlanType } from "../../models/enumerations/user-limit";
import { LicenseLimitPlanType } from "../../models/enumerations/license-limit";
import FunctionService from "../../utilities/services/function-service";
import moment from "moment";
import { PlanIdPlanType } from "../../models/enumerations/plan-id";
import { TextTypeInput } from "../forms";
import { useForm } from "react-hook-form";
import { createLogger } from "../../utilities/logger";
import { useTranslation } from "react-i18next";
import { PlanTypeLabels } from "../../models/plan-type";
import { Price } from "../../models/interfaces/price";
import { Product } from "../../models/interfaces/product";
import { OrganizationSubscription } from "../../models/interfaces/organization-subscription";
import { PaymentCycleLabel } from "../../models/payment-cycle";
import { OrganizationType, OrganizationTypeLabel } from "../../models/enumerations/organization-type";
import { deleteField } from "firebase/firestore";
import { RadioGroupItem } from "../radio-group/radio-group-item";
import BillingService from "../../utilities/services/billing-service";

const log = createLogger("CheckoutFormUpdate");

const COMPONENT_CLASS = "c-checkoutform";
const FORM_COMPONENT_CLASS = "p-link-create-account";

interface CheckoutFormUpdateProps {
  setUpgradeModalOpen: Function;
  onChangePlan?: () => void;
  returnUrl: string;
  paymentFailed: Function;
  paymentSuccessful: Function;
  paymentCycle: PaymentCycle;
  selectedProduct: Product;
  selectedPrice: Price;
  isLoading: boolean;
  setIsLoading: React.Dispatch<React.SetStateAction<boolean>>;
  orgSub: OrganizationSubscription;
}

const CheckoutFormUpdate: React.FC<CheckoutFormUpdateProps> = ({
  selectedPrice,
  selectedProduct,
  setUpgradeModalOpen,
  onChangePlan,
  paymentSuccessful,
  paymentFailed,
  returnUrl,
  paymentCycle,
  isLoading,
  setIsLoading,
  orgSub,
}) => {
  const { t } = useTranslation();
  const { state } = useAuthState();
  const stripe = useStripe();
  const elements = useElements();
  const [clientSecret, setClientSecret] = useState("");
  const [customerId, setCustomerId] = useState<string>();
  const [planType, setPlanType] = useState<PlanType>(
    selectedProduct.id ? PlanIdPlanType[selectedProduct.id] : PlanType.Core
  );
  const [paymentFailedBanner, setPaymentFailedBanner] = useState<boolean>(false);
  const [newPrice, setNewPrice] = useState<number>();
  const [totalPrice, setTotalPrice] = useState<number>();
  const [proratedSum, setProratedSum] = useState<number>();
  const [discountAmount, setDiscountAmount] = useState<number>();
  const [coupons, setCoupons] = useState<any[]>([]);
  const [subscriptionCoupon, setSubscriptionCoupon] = useState<string>();
  const [couponValue, setCouponValue] = useState<any>();
  const [discountError, setDiscountError] = useState<string>("");
  const [couponExists, setCouponExists] = useState<boolean>(true);
  const params = new URLSearchParams(window.location.search);
  const couponId = params.get("couponId");

  const methods = useForm<any>();
  const { register } = methods;

  let priceToShow = "";
  if (selectedPrice && selectedPrice.unit_amount) {
    priceToShow = (selectedPrice.unit_amount / 100).toFixed(2);
  }

  if (elements) {
    const element = elements!.getElement("payment");
    if (element) {
      element.on("ready", () => {
        setIsLoading(false);
      });
    }
  }

  useEffect(() => {
    if (selectedProduct.id && selectedPrice.unit_amount !== undefined) {
      const purchasedPlanType = PlanIdPlanType[selectedProduct.id];
      setPlanType(purchasedPlanType);
      const price = Number((selectedPrice.unit_amount / 100).toFixed(2));
      setTotalPrice(price);
    }
  }, [selectedPrice.unit_amount, selectedProduct.id]);

  // Proration preview
  useEffect(() => {
    const getFinalPrice = async () => {
      if (!state.organization.id || !orgSub.stripePlanSubscription) {
        return;
      }

      if (!selectedPrice.id) {
        return;
      }

      try {
        const preview = await BillingService.getProrationPreview({
          currentSubscriptionId: orgSub.stripePlanSubscription,
          newPriceId: selectedPrice.id,
          couponId: subscriptionCoupon,
        });

        setNewPrice(preview.amountDue);
        setProratedSum(preview.proratedSum);
        setDiscountAmount(preview.discountAmount);
      } catch (error) {
        log.error("proration preview error", {
          exception: error,
          organizationId: state.organization?.id,
        });
      }
    };

    getFinalPrice();
  }, [selectedPrice.id, state.organization.id, subscriptionCoupon]);

  // Confirm setup intent after clientSecret is set
  useEffect(() => {
    const confirmIntent = async () => {
      if (!clientSecret || !customerId || !stripe || !elements) {
        return;
      }

      setIsLoading(true);

      const { error, setupIntent } = await stripe.confirmSetup({
        elements,
        clientSecret,
        confirmParams: {
          return_url: `${window.location.origin}${returnUrl}`,
        },
        redirect: "if_required",
      });

      if (error) {
        log.error("Stripe confirmSetup error during plan update", {
          exception: error,
          customerId,
          subscriptionId: orgSub?.stripePlanSubscription,
        });
        setPaymentFailedBanner(true);
        paymentFailed(true);
        setIsLoading(false);
        return;
      }

      const paymentMethodId =
        typeof setupIntent.payment_method === "string"
          ? setupIntent.payment_method
          : undefined;

      if (!paymentMethodId || !orgSub.stripePlanSubscription) {
        log.error("no payment method or subscription, failed banner", {
          organizationId: state.organization?.id,
          subscriptionId: orgSub?.stripePlanSubscription,
          missing: {
            paymentMethodId: !paymentMethodId,
            stripePlanSubscription: !orgSub.stripePlanSubscription,
          },
        });
        setPaymentFailedBanner(true);
        paymentFailed(true);
        setIsLoading(false);
        return;
      }

      try {
        if (!selectedPrice.id) {
          log.warn("Plan update blocked: no price selected", {
            organizationId: state.organization?.id,
          });
          setPaymentFailedBanner(true);
          paymentFailed(true);
          setIsLoading(false);
          return;
        }

        const response = await BillingService.completeCheckoutUpdate({
          customerId,
          paymentMethodId,
          currentSubscriptionId: orgSub.stripePlanSubscription,
          newPriceId: selectedPrice.id,
          couponId: subscriptionCoupon,
        });

        const renewDate = new Date(response.currentPeriodEnd * 1000);
        const nextPaymentDate = `${renewDate.toLocaleString("default", { month: "short" })} ${renewDate.getDate()}, ${renewDate.getFullYear()}`;

        let couponDiscountAmount = "";
        let copyOfTotalDiscount = 0;

        let paymentInfo = {
          planType: PlanTypeLabels[planType],
          paymentCycle: paymentCycle !== undefined ? PaymentCycleLabel[paymentCycle] : paymentCycle,
          nextPaymentDate,
          planCost: `$${selectedPrice && selectedPrice.unit_amount !== undefined ? (selectedPrice.unit_amount / 100).toFixed(2) : totalPrice!.toFixed(2)}`,
          totalChargedAmount: newPrice ? `$${newPrice.toFixed(2)}` : `$${totalPrice!.toFixed(2)}`,
        };

        if (coupons.length > 0 && coupons[0].discountAmount) {
          couponDiscountAmount = `$${Number(coupons[0].discountAmount).toFixed(2)}`;
          copyOfTotalDiscount = Number(coupons[0].discountAmount);
        }

        const copyOfTotalCharged = newPrice ? newPrice : totalPrice ? totalPrice : 0;
        const updatedTotal = copyOfTotalCharged - copyOfTotalDiscount;
        paymentInfo = { ...paymentInfo, totalChargedAmount: `$${updatedTotal.toFixed(2)}` };

        const couponInfo = coupons.length > 0
          ? {
            couponCode: coupons[0].code,
            couponDiscount: coupons[0].discountLabel,
            couponDiscountAmount,
          }
          : null;

        try {
          FunctionService.sendPlanPurchaseConfirmationEmail(state.user!, paymentInfo, couponInfo);
        } catch (err) {
          log.error("Failed to send plan purchase confirmation email", {
            exception: err,
          });
        }

        const org = await OrganizationService.get(state.organization.id!);
        org!.plan = { value: planType, label: PlanTypeLabels[planType] };
        org!.userLimit = UserLimitPlanType[planType];
        org!.mySymmioLicense = LicenseLimitPlanType[planType];
        await OrganizationService.save(org!);

        if (org && org.id) {
          orgSub.renewDate = renewDate.toISOString();
          orgSub.userLimit = UserLimitPlanType[planType];
          orgSub.price = response.planAmount || 0;
          orgSub.plan = {
            label: PlanTypeLabels[planType],
            value: planType,
          };
          orgSub.paymentCycle = {
            label: PaymentCycle[paymentCycle === PaymentCycle.Yearly ? PaymentCycle.Yearly : PaymentCycle.Monthly],
            value: paymentCycle === PaymentCycle.Yearly ? PaymentCycle.Yearly : PaymentCycle.Monthly,
          };
          orgSub.mySymmioLicense = LicenseLimitPlanType[planType];
          orgSub.accountType = {
            label: OrganizationTypeLabel[OrganizationType.Paid],
            value: OrganizationType.Paid,
          };
          orgSub.trialLength = deleteField() as unknown as RadioGroupItem;
          await OrganizationSubscriptionService.save(orgSub);
        }

        log.info("Plan updated", {
          planType,
          customerId,
          organizationId: state.organization?.id,
          subscriptionId: orgSub?.stripePlanSubscription,
          newPriceId: selectedPrice.id,
          price: response.planAmount || 0,
          paymentCycle: paymentCycle === PaymentCycle.Yearly ? "yearly" : "monthly",
          couponId: subscriptionCoupon,
        });

        paymentSuccessful(true);
      } catch (err) {
        log.error("confirm Intent error", {
          exception: err,
          subscriptionId: orgSub?.stripePlanSubscription,
        });
        setPaymentFailedBanner(true);
        paymentFailed(true);
      } finally {
        setIsLoading(false);
      }
    };

    confirmIntent();
  }, [clientSecret, customerId, stripe, elements]);

  const handleSubmit = (e: { preventDefault: () => void }) => {
    e.preventDefault();
    handleSubscriptionStart();
  };

  const handleSubscriptionStart = async () => {
    if (!stripe || !elements) {
      return;
    }

    const { error: submitError } = await elements.submit();
    if (submitError) {
      log.error("Stripe elements submit error during plan update", {
        exception: submitError,
      });
      return;
    }

    setIsLoading(true);

    try {
      const response = await BillingService.createUpdateSetupIntent({
        email: state.user?.email!,
      });

      setCustomerId(response.customerId);
      setClientSecret(response.clientSecret);
    } catch (error) {
      log.error("handleSubscriptionStart error", {
        exception: error,
      });
      setPaymentFailedBanner(true);
      paymentFailed(true);
    } finally {
      setIsLoading(false);
    }
  };

  const handleApplyCode = async () => {
    setDiscountError("");

    if (coupons.length > 0) {
      setDiscountError(t("pricing.checkoutPage.discountErrors.only1"));
      return;
    }

    if (!couponValue) {
      setDiscountError(t("pricing.checkoutPage.discountErrors.enterValid"));
      return;
    }

    if (!selectedProduct.id) {
      return;
    }

    const result = await BillingService.validatePromotionCode({
      code: couponValue,
      customerId,
      productId: selectedProduct.id,
      paymentCycle,
      totalPrice: Number(totalPrice),
    }).catch((e) => {
      log.error("Failed to validate promotion code", {
        exception: e,
        couponValue,
        customerId,
      });
      return null;
    });

    if (!result || !result.valid || !result.couponId) {
      setDiscountError(result?.message || t("pricing.checkoutPage.discountErrors.enterValid"));
      return;
    }

    setCoupons([result]);
    setSubscriptionCoupon(result.couponId);
    setCouponValue("");
  };

  useEffect(() => {
    const resolveCoupon = async () => {
      if (!couponId || !totalPrice || !selectedProduct?.id || paymentCycle == null) {
        setCouponExists(false);
        return;
      }

      const result = await BillingService.resolvePromotionCode({
        couponId,
        productId: selectedProduct.id,
        paymentCycle,
        totalPrice: Number(totalPrice),
      }).catch((e) => {
        log.error("Failed to resolve promotion code", {
          exception: e,
          couponId,
        });
        return null;
      });

      if (!result || !result.valid || !result.couponId) {
        setCouponExists(false);
        return;
      }

      setSubscriptionCoupon(result.couponId);
    };

    resolveCoupon();
  }, [couponId, paymentCycle, selectedProduct?.id, totalPrice]);

  return (
    <div className={`${COMPONENT_CLASS}`}>
      <Loader isVisible={isLoading} />
      {paymentFailedBanner && (
        <div style={{ width: "90%", marginLeft: "auto", marginRight: "auto" }}>
          <WarningItem
            iconColor="#E7514F"
            onClose={() => { setPaymentFailedBanner(false); }}
            backgroundColor="#FFE9E5"
            title={t("pricing.checkoutPage.paymentDeclined.banner.title")}
            text={t("pricing.checkoutPage.paymentDeclined.banner.subText")}
          />
        </div>
      )}

      <div className={`${COMPONENT_CLASS}__container-modal`}>
        <div className={`${COMPONENT_CLASS}__summary`}>
          <div className={`${COMPONENT_CLASS}_summary_line`}>
            <div style={{ fontSize: "20px" }}>{selectedProduct.name ?? ""}</div>
            <div style={{ textDecoration: "underline", cursor: "pointer" }} onClick={onChangePlan}>
              {t("pricing.checkoutPage.changePlan")}
            </div>
          </div>
          {state.organization && state.organization.plan && state.organization.plan.value !== undefined && (
            <div style={{ flexDirection: "column", alignItems: "start" }} className={`${COMPONENT_CLASS}_summary_line`}>
              <div style={{ fontWeight: "bold" }}>{t("pricing.checkoutPage.accChange")}</div>
              <ul style={{ marginLeft: "1rem", marginTop: "1rem", listStyleType: "disc" }}>
                <li>
                  {UserLimitPlanType[state.organization.plan.value] === UserLimitPlanType[planType]
                    ? t("pricing.checkoutPage.userLimitSame", { limit: UserLimitPlanType[planType] })
                    : t("pricing.checkoutPage.userLimit", {
                      starterLimit: UserLimitPlanType[state.organization.plan.value],
                      businessLimit: UserLimitPlanType[planType],
                    })}
                </li>
                <li>
                  {LicenseLimitPlanType[state.organization.plan.value] === LicenseLimitPlanType[planType]
                    ? t("pricing.checkoutPage.licenseLimitSame", { limit: LicenseLimitPlanType[planType] })
                    : t("pricing.checkoutPage.licenseLimit", {
                      starterLimit: LicenseLimitPlanType[state.organization.plan.value],
                      businessLimit: LicenseLimitPlanType[planType],
                    })}
                </li>
                <li>
                  {paymentCycle === PaymentCycle.Yearly
                    ? t("pricing.checkoutPage.year", { price: priceToShow, date: moment(orgSub?.renewDate).format("MMMM D Y") })
                    : t("pricing.checkoutPage.month", { price: priceToShow, date: moment(orgSub?.renewDate).format("MMMM D Y") })}
                </li>
              </ul>
            </div>
          )}
          {proratedSum !== undefined && (
            <>
              <hr className={`${COMPONENT_CLASS}_divider ${COMPONENT_CLASS}__upgrade-divider`} />
              <div className={`${COMPONENT_CLASS}_summary_line`}>
                <div style={{ fontWeight: "bold" }}>{t("pricing.checkoutPage.credit")}</div>
                <div>${proratedSum.toFixed(2)}</div>
              </div>
            </>
          )}
          {discountAmount !== undefined && (
            <>
              <hr className={`${COMPONENT_CLASS}_divider ${COMPONENT_CLASS}__upgrade-divider`} />
              <div className={`${COMPONENT_CLASS}_summary_line`}>
                <div style={{ fontWeight: "bold" }}>{t("pricing.checkoutPage.discountAmount")}</div>
                <div>${discountAmount.toFixed(2)}</div>
              </div>
            </>
          )}
          <hr className={`${COMPONENT_CLASS}_divider ${COMPONENT_CLASS}__upgrade-divider`} />
          <div className={`${COMPONENT_CLASS}_summary_line`}>
            <div style={{ fontWeight: "bold" }}>{t("pricing.checkoutPage.todayPay")}</div>
            {newPrice !== undefined && newPrice !== totalPrice ? (
              <div style={{ display: "flex" }}>
                <div style={{ textDecoration: "line-through", marginRight: "0.5rem", color: "gray" }}>
                  ${totalPrice?.toFixed(2) ?? "..."}
                </div>
                <div>${newPrice.toFixed(2) ?? "..."}</div>
              </div>
            ) : (
              <div>${totalPrice?.toFixed(2) ?? "..."}</div>
            )}
          </div>
          {!couponExists && (
            <>
              <form style={{ display: "flex", justifyContent: "space-between", gap: "1rem", padding: "0rem 1.25rem 1.25rem 1.25rem" }}>
                <div style={{ fontWeight: "300" }}>
                  <TextTypeInput
                    id="discountCode"
                    type="text"
                    label={t("pricing.checkoutPage.discount")}
                    inputClassName={`${`${COMPONENT_CLASS}__discount-field`} ${(discountError) && `${FORM_COMPONENT_CLASS} -showerror`}`}
                    hideLabel={true}
                    style={{ width: "100%" }}
                    registerHook={register}
                    registerOptions={{
                      value: couponValue,
                      onChange: (e) => { setCouponValue(e.target.value); },
                      onBlur: (e) => { if (discountError && e.target.value === "") { setDiscountError(""); } },
                    }}
                  />
                </div>
                {discountError && (
                  <div role="alert" className={`${FORM_COMPONENT_CLASS} -coupon-error`}>
                    {discountError}
                  </div>
                )}
                <div>
                  <Button type="default" disabled={isLoading} buttonType="button" onClick={handleApplyCode} buttonText="Apply" />
                </div>
              </form>
              <div style={{ marginLeft: "1.25rem", marginRight: "1.25rem" }}>
                {coupons.map((coupon, i) => (
                  <TagComponent
                    id={i.toString()}
                    onCloseClick={(index) => {
                      const newCoupons = [...coupons];
                      newCoupons.splice(parseInt(index), 1);
                      setCoupons(newCoupons);
                      setSubscriptionCoupon(undefined);
                    }}
                    key={`coupon-${i}`}
                  >
                    {coupon.code}
                  </TagComponent>
                ))}
              </div>
            </>
          )}
        </div>
        <form onSubmit={handleSubmit} className={`${COMPONENT_CLASS}__form`} id="checkout-form">
          <PaymentElement />
        </form>
      </div>

      <div
        className={`${COMPONENT_CLASS}__bottom-text-modal`}
        dangerouslySetInnerHTML={{ __html: t("pricing.checkoutPage.termsAndConditions") }}
      />
      <div className={`${COMPONENT_CLASS}__start-subscription`}>
        <Button
          type="link"
          inputClassName={`${COMPONENT_CLASS}__actions__cancel`}
          buttonText={t("pricing.checkoutPage.keepCurrent")}
          onClick={() => {
            setUpgradeModalOpen(false);
          }}
          disabled={isLoading}
        />
        <Button
          buttonType="submit"
          form="checkout-form"
          inputClassName="action-button"
          buttonText={t("pricing.checkoutPage.payAndUpgrade")}
          disabled={isLoading}
        />
      </div>
    </div>
  );
};

export default CheckoutFormUpdate;